import { router, useGlobalSearchParams } from 'expo-router';
import type { ReactNode } from 'react';

import { Button, Text, VStack, theme } from '@squire/ui';

import { routes } from '@/constants/routes';
import { useScreenTranslations } from '@/hooks/useScreenTranslations';

import { styles } from './styles';
import type { ViewOrderBottomSheetActionsProps } from './types';

const ViewOrderBottomSheetActions = ({
  stage,
  dismissBottomSheet,
  isCreatingCart,
  onContinueToBookingConsent,
  onChooseTime,
  shop,
  cart,
  waitlistSelectedIds,
  isStandbyWaitlist = false,
}: ViewOrderBottomSheetActionsProps) => {
  const { t } = useScreenTranslations('ViewOrderBottomSheet');
  const { t: tActions } = useScreenTranslations('ViewOrderBottomSheetActions');

  const params = useGlobalSearchParams();

  const appointments = cart?.items || [];

  const allAppointmentsHaveServices =
    appointments.length > 0 &&
    appointments.every((appointment) => appointment.services.length > 0);

  const lastAppointment = appointments.at(-1);
  const barberId = lastAppointment?.bookedWithAnyBarber
    ? 'any'
    : lastAppointment?.barber?.route || lastAppointment?.barberId || '';

  const navigateToChooseTimeWithAnyTime = () => {
    dismissBottomSheet?.();
    onChooseTime?.();
    router.push(
      routes.chooseTime({
        ...params,
        barberId,
        anyTime: true,
        shopRoute: shop?.route || '',
        cart: cart?.id,
      }),
    );
  };

  const getActionButtons = () => {
    const buttons: ReactNode[] = [];

    // Cart must exist on the server (id assigned) before showing nav buttons;
    // otherwise navigating pushes ChooseTimeScreen with cartId=undefined and
    // dismisses the user out of the booking flow on Android.
    if (
      stage === 'choose-service' &&
      allAppointmentsHaveServices &&
      !!cart?.id
    ) {
      buttons.push(
        <Button
          key="chooseTime"
          testID="choose-time-button"
          disabled={isCreatingCart}
          onPress={navigateToChooseTimeWithAnyTime}
          style={styles.timeButton}
          accessibilityRole="button"
          accessibilityLabel={tActions('accessibility.chooseTimeButton')}
          accessibilityHint={tActions('accessibility.chooseTimeButtonHint')}
        >
          {t('chooseTime')}
        </Button>,
      );
    }

    if (stage === 'choose-time') {
      // Show Continue button if all appointments either have a time selected OR are on the waitlist
      const allAppointmentsReady = appointments.every(
        (a) => a.dateTime || waitlistSelectedIds?.has(a.id),
      );

      if (allAppointmentsReady) {
        buttons.push(
          <Button
            key="continue"
            testID="continue-button"
            disabled={isCreatingCart}
            onPress={onContinueToBookingConsent}
            style={styles.timeButton}
            accessibilityRole="button"
            accessibilityLabel={tActions('accessibility.continueButton')}
            accessibilityHint={tActions('accessibility.continueButtonHint')}
          >
            {tActions('continue')}
          </Button>,
        );
      }
    }

    return buttons;
  };

  const actions = getActionButtons();

  const hasWaitlistItems =
    stage === 'choose-time' &&
    !isStandbyWaitlist &&
    appointments.some((a) => waitlistSelectedIds?.has(a.id));

  if (!actions.length) return null;
  return (
    <VStack space={theme.spacing[3]}>
      {actions}
      {hasWaitlistItems && (
        <Text variant="s" color="cartTextTertiary" align="center">
          {tActions('waitlistDisclaimer')}
        </Text>
      )}
    </VStack>
  );
};

ViewOrderBottomSheetActions.displayName = 'ViewOrderBottomSheetActions';
export { ViewOrderBottomSheetActions };
